import type { LensCardData } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';

import { csvMeasure } from '../csvValues';

import { type Row, commsMetrics, number, ratio } from './liveModel';

export const worksheetFields = [
  'sheet',
  'bucket',
  'split',
  'detail',
  'offer_label',
  'vendor_name',
  'email_placement',
  'send_date',
  'deal_tier',
  'total_clicks',
  'unique_clicks',
  'items',
  'ttv',
  'gm',
];
export const sendFields = [
  'bucket',
  'split',
  'send_cnt',
  'unique_send_cnt',
  'open_cnt',
  'unique_open_cnt',
  'click_cnt',
  'unique_click_cnt',
  'unsubscribe_cnt',
  'unique_unsubscribe_cnt',
  'sessions',
  'unique_sessions',
  'orders',
  'ttv',
  'gm',
];

/** Fail closed on missing, duplicate or malformed batches, including empty payloads. */
export function completeWorksheetCard(card: LensCardData): LensCardData {
  const fields =
    card.cardId === 'worksheet_rows' ? worksheetFields : card.cardId === 'sends_rows' ? sendFields : undefined;
  if (!fields) return card;
  if (card.truncated) return { ...card, rows: [] };
  if (!card.rows?.length) return card;
  try {
    let expected: number | undefined;
    const ids = new Set<number>();
    const rows = card.rows.flatMap((batch) => {
      const id = number(batch.batch),
        count = number(batch.record_count),
        total = number(batch.source_count);
      if (
        id === null ||
        count === null ||
        total === null ||
        !Number.isSafeInteger(id) ||
        id < 0 ||
        ids.has(id) ||
        !Number.isSafeInteger(count) ||
        count < 1 ||
        !Number.isSafeInteger(total) ||
        total < count ||
        (expected !== undefined && total !== expected)
      )
        throw Error('Invalid worksheet batch counts.');
      ids.add(id);
      expected = total;
      if (typeof batch.payload !== 'string') throw Error('Missing worksheet payload.');
      const payload: unknown = JSON.parse(batch.payload);
      if (!Array.isArray(payload) || payload.length !== count) throw Error('Invalid worksheet payload count.');
      return payload.map((row) => {
        if (!Array.isArray(row) || row.length !== fields.length) throw Error('Invalid worksheet row.');
        return Object.fromEntries(fields.map((key, i) => [key, row[i]]));
      });
    });
    if (rows.length !== expected || [...ids].some((id) => id >= ids.size))
      throw Error('Incomplete worksheet population.');
    return { ...card, rows };
  } catch (error) {
    return { ...card, rows: [], status: 'error', message: (error as Error).message };
  }
}

/** Worksheets shown on each click tab, in Tableau's order. */
export const WORKSHEET_ROLES: Record<string, Array<string>> = {
  clicks: ['Content + Position', 'GM by Vendor', 'GM by Vendor (2)'],
  regions: [
    'Clicks by Country',
    'GM by Vendor (AU)',
    'GM by Vendor (NSW)',
    'GM by Vendor (VIC)',
    'GM by Vendor (NZ)',
    'GM by Vendor (US)',
    'GM by Vendor (UK)',
    'Singapore Clicks',
    'Hong Kong Clicks',
    'GM by Vendor (2)',
  ],
  offers: ['Offers Clicks Table', 'Offers Click Graph'],
  vendors: ['Offer Vendor'],
};

export const WORKSHEET_CSV_COLUMNS = [...worksheetFields, 'clicks', 'conversion', 'gm_per_click'];
// Money to the cent and conversion as a six-place fraction in the CSV; everything else as stored.
const WORKSHEET_CSV_KINDS: Record<string, string> = {
  ttv: 'money',
  gm: 'money',
  gm_per_click: 'money',
  conversion: 'rate',
};

/** One CSV of worksheet rows: a metadata line, the column names, then each row with its derived click measures. */
export function worksheetCsv(rows: Array<Row>, unique: boolean, metadata: Array<string>): string {
  return (
    '\uFEFF' +
    [
      metadata,
      WORKSHEET_CSV_COLUMNS,
      ...rows.map((row) => {
        const derived = worksheetMetrics(row, unique);
        return WORKSHEET_CSV_COLUMNS.map((key) => csvMeasure(derived[key], WORKSHEET_CSV_KINDS[key] ?? 'value', 3));
      }),
    ]
      .map((row) => row.map((value) => csvCell(value, 'Unknown')).join(','))
      .join('\r\n') +
    '\r\n'
  );
}

/** Every worksheet row behind one click tab, in worksheet order. */
export function tabWorksheetRows(rows: Array<Row>, view: string): Array<Row> {
  return (WORKSHEET_ROLES[view] ?? []).flatMap((sheet) => rows.filter((row) => row.sheet === sheet));
}

export function worksheetMetrics(row: Row, unique: boolean): Row {
  const clicks = number(row[unique ? 'unique_clicks' : 'total_clicks']);
  return { ...row, clicks, conversion: ratio(row.items, clicks), gm_per_click: ratio(row.gm, clicks) };
}

/** Each cell retains its warehouse operand sums; ratios are derived after aggregation. */
export function commsCrosstab(rows: Array<Row>, unique: boolean) {
  const buckets = [...new Set(rows.map((r) => String(r.bucket ?? 'None')))].sort();
  const splits = [...new Set(rows.map((r) => String(r.split ?? 'All')))].sort();
  const cells = new Map(
    rows.map((row) => [
      JSON.stringify([String(row.split ?? 'All'), String(row.bucket ?? 'None')]),
      commsMetrics(row, unique),
    ]),
  );
  return { buckets, splits, cell: (split: string, bucket: string) => cells.get(JSON.stringify([split, bucket])) };
}

export type OfferSelection = { source: 'table' | 'graph'; values: Row };
export function linkedOfferRows(rows: Array<Row>, selection: OfferSelection | null): Array<Row> {
  if (!selection) return rows;
  return rows.filter((row) => {
    // A selected source sheet remains intact; the other sheet receives all source fields.
    const sourceSheet = selection.source === 'table' ? 'Offers Clicks Table' : 'Offers Click Graph';
    if (row.sheet === sourceSheet) return true;
    const fields =
      selection.source === 'table'
        ? ['offer_label', 'vendor_name', 'email_placement', 'send_date']
        : ['split', 'bucket'];
    return fields.every((key) => {
      const targetKey = key === 'split' ? 'email_placement' : key;
      return row[targetKey] === selection.values[key];
    });
  });
}
